import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-home.webp.asset.json";
const heroImg = heroAsset.url;
import { inspectorImg } from "@/lib/inspector-image";
import areasImg from "@/assets/areas.jpg";
import { Section, SectionHeading, TrustBar, FaqAccordion, Eyebrow, CtaBand } from "@/components/sections";
import { QuoteForm } from "@/components/QuoteForm";
import { SITE, SERVICE_AREAS, SECONDARY_SERVICES, faqJsonLd, type FaqItem, breadcrumbJsonLd } from "@/lib/site";
import { Home, ClipboardCheck, MessageSquare, Calendar, Star } from "lucide-react";

const faqs: FaqItem[] = [
  { q: "What services does Anchor Services offer?", a: "We're a complete property care company: Home Watch inspections, junk removal, estate and property cleanouts, garage cleanouts, foreclosure and eviction cleanouts, storm debris removal, furniture and appliance removal, and personal concierge services." },
  { q: "Can I book more than one service at a time?", a: "Yes — most clients do. A single call can cover a cleanout, a junk haul and ongoing Home Watch visits, all handled by the same crew with one point of contact and one invoice." },
  { q: "What exactly is Home Watch?", a: "Home Watch is a visual inspection of your unoccupied home looking for obvious issues — leaks, storm damage, pest activity, HVAC problems and security concerns — followed by a detailed report with photos so you have peace of mind while you're away." },
  { q: "How fast can you schedule junk removal or a cleanout?", a: "Most junk removal and small cleanout jobs can be scheduled same-day or next-day. Larger estate and property cleanouts are usually booked within a few days of your on-site estimate." },
  { q: "Do you only serve Sarasota?", a: "We serve Sarasota, Venice, Nokomis, Osprey, Lakewood Ranch, Palmer Ranch, Siesta Key, Longboat Key and Bradenton." },
  { q: "Are you licensed and insured?", a: "Yes. Anchor Services is fully licensed, insured and bonded — and every team member is background checked before they ever set foot on your property." },
  { q: "How do I get a quote?", a: "Use our short quote form or call us. We'll talk through your property and schedule, then send a written proposal — usually within one business day." },
];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://anchorsrq.com/#business",
  name: "Anchor Services",
  url: "https://anchorsrq.com",
  telephone: "+1-941-500-2320",
  email: "info@anchorsrq.com",
  priceRange: "$$",
  openingHours: "Mo-Sa 08:00-18:00",
  areaServed: [
    "Sarasota",
    "Siesta Key",
    "Longboat Key",
    "Lakewood Ranch",
    "Bradenton",
    "Venice",
    "Osprey",
    "Nokomis",
  ].map((name) => ({ "@type": "City", name })),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Property Care Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Home Watch Services",
          description: "Professional home monitoring and property care for seasonal residents and absentee homeowners.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Property Inspections",
          description: "Visual interior and exterior inspections looking for leaks, storm damage, pest activity and security concerns.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Junk Removal",
          description: "Fast, full-service junk hauling for homes and properties.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Estate Cleanouts",
          description: "Compassionate, complete estate cleanouts for families and executors.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Property Cleanouts",
          description: "Full-property clearing for landlords, agents, and owners.",
        },
      },
    ],
  },
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Sarasota Property Care, Home Watch & Junk Removal | Anchor Services" },
      { name: "description", content: "Anchor Services is Sarasota's complete property care company — Home Watch inspections, junk removal, estate and property cleanouts, storm debris hauling and concierge services." },
      { property: "og:title", content: "Complete Property Care in Sarasota | Anchor Services" },
      { property: "og:description", content: "Home Watch, junk removal, cleanouts, storm debris hauling and concierge services across Sarasota, Venice, Lakewood Ranch, Siesta Key and Longboat Key." },
      { property: "og:url", content: "https://anchorsrq.com/" },
    ],
    links: [
      { rel: "canonical", href: "https://anchorsrq.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd([{ name: 'Home', url: 'https://anchorsrq.com/' }])) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={heroImg} alt="Luxury Sarasota waterfront home at sunset" width={1600} height={900} fetchPriority="high" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[oklch(0.22_0.04_250)]/85 via-[oklch(0.22_0.04_250)]/55 to-[oklch(0.22_0.04_250)]/85" />
        </div>
        <div className="mx-auto max-w-7xl px-4 py-24 text-primary-foreground sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="max-w-2xl">
            <Eyebrow><span className="text-[oklch(0.85_0.12_82)]">Sarasota · Venice · Lakewood Ranch · Longboat Key</span></Eyebrow>
            <h1 className="font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
              Complete Property Care for Sarasota Homeowners
            </h1>
            <p className="mt-5 max-w-xl text-base text-primary-foreground/85 sm:text-lg">
              One trusted local team for Home Watch inspections, junk removal, estate and property cleanouts, storm debris hauling, furniture and appliance removal, and personal concierge services.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="rounded-md bg-[oklch(0.78_0.12_82)] px-6 py-3.5 text-sm font-semibold text-[oklch(0.22_0.04_250)] shadow-sm transition hover:brightness-105">
                Get a Free Quote
              </Link>
              <a href="tel:+19415002320" className="rounded-md border border-primary-foreground/30 px-6 py-3.5 text-sm font-medium text-primary-foreground hover:bg-primary-foreground/10">
                Call (941) 500-2320
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* All services */}
      <Section tone="muted" id="services">
        <SectionHeading
          eyebrow="Our Services"
          title="Everything your Sarasota property needs — under one roof."
          intro="From ongoing Home Watch inspections to a same-week cleanout or a single furniture pickup, Anchor Services handles it with one crew you can trust."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/home-watch-services" className="group rounded-xl border border-border bg-card p-6 transition hover:border-[oklch(0.78_0.12_82)] hover:shadow-sm">
            <div className="font-serif text-lg text-foreground group-hover:text-primary">Home Watch Services</div>
            <p className="mt-2 text-sm text-muted-foreground">Weekly, bi-weekly and monthly inspections of unoccupied homes, with a photo report after every visit.</p>
            <div className="mt-4 text-xs font-medium uppercase tracking-wider text-[oklch(0.55_0.12_82)]">Learn more →</div>
          </Link>
          {SECONDARY_SERVICES.map((s) => (
            <Link key={s.slug} to={`/${s.slug}` as string} className="group rounded-xl border border-border bg-card p-6 transition hover:border-[oklch(0.78_0.12_82)] hover:shadow-sm">
              <div className="font-serif text-lg text-foreground group-hover:text-primary">{s.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.short}</p>
              <div className="mt-4 text-xs font-medium uppercase tracking-wider text-[oklch(0.55_0.12_82)]">Learn more →</div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Why Anchor */}
      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Why Anchor Services</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">One local team for the whole property.</h2>
            <p className="mt-4 text-muted-foreground">
              Most homeowners juggle a handful of vendors — one for hauling, one for cleanouts, someone else to check on the house. Anchor Services replaces all of it. We're licensed, insured, bonded and local, and every job is done by background-checked crews who treat your property like their own.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Home Watch, hauling, cleanouts and concierge — one point of contact.",
                "Upfront pricing and written estimates before any work starts.",
                "Photo documentation on every visit and every job.",
                "Fast scheduling, including same-day and next-day hauling.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-foreground/85">
                  <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[oklch(0.78_0.12_82)]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={inspectorImg.src} srcSet={inspectorImg.srcSet} sizes={inspectorImg.sizes} alt="Anchor Services team member reviewing a tablet at a Sarasota home" width={inspectorImg.width} height={inspectorImg.height} loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-2xl object-cover shadow-lg" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-card p-4 shadow-md sm:block">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Serving Sarasota</div>
              <div className="font-serif text-2xl text-primary">Licensed & insured</div>
            </div>
          </div>
        </div>
      </Section>

      {/* Process */}
      <Section tone="muted">
        <SectionHeading eyebrow="Our Process" title="How working with Anchor Services works" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { i: MessageSquare, t: "1. Tell us what you need", d: "Call or send the quote form — Home Watch, a cleanout, a haul, or all three." },
            { i: ClipboardCheck, t: "2. Free estimate", d: "We walk the property or review your photos and give you a written price." },
            { i: Calendar, t: "3. We schedule it", d: "Recurring visits or a one-time job, on the date that works for you." },
            { i: Home, t: "4. Done & documented", d: "Photos and a written summary so you always know what happened." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="relative rounded-xl border border-border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[oklch(0.78_0.12_82)]/20 text-[oklch(0.55_0.12_82)]">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-serif text-lg">{t}</div>
              <p className="mt-1.5 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Service areas */}
      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Service Areas</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Trusted across the Sarasota coast.</h2>
            <p className="mt-4 text-muted-foreground">
              Anchor Services proudly serves homeowners, landlords and agents from downtown Sarasota to the barrier islands and east into Lakewood Ranch.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {SERVICE_AREAS.map((a) => (
                <Link key={a} to="/service-areas" className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground/80 hover:border-[oklch(0.78_0.12_82)]">
                  {a}
                </Link>
              ))}
            </div>
          </div>
          <img src={areasImg} alt="Aerial view of waterfront Sarasota neighborhoods" width={1600} height={1000} loading="lazy" className="rounded-2xl object-cover shadow-lg" />
        </div>
      </Section>

      {/* Testimonials */}
      <Section tone="muted">
        <SectionHeading eyebrow="What clients say" title="Peace of mind, in their words." />
        {/* TODO: replace with real reviews */}
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {[
            { n: "Margaret H.", c: "Longboat Key", t: "We spend summers up north and Anchor's weekly reports give us total peace of mind. Caught a slow leak under the sink before it turned into a disaster." },
            { n: "David & Susan R.", c: "Lakewood Ranch", t: "Professional, reliable and genuinely cared about our home. After Hurricane season the photo report was incredibly thorough." },
            { n: "Robert K.", c: "Siesta Key", t: "Best decision we made when we bought our second home. They coordinated with our HVAC tech and the cleaning crew without us lifting a finger." },
          ].map((r) => (
            <div key={r.n} className="rounded-xl border border-border bg-card p-6">
              <div className="flex gap-0.5 text-[oklch(0.78_0.12_82)]">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground/85">"{r.t}"</p>
              <div className="mt-5 text-sm font-medium text-foreground">{r.n}</div>
              <div className="text-xs text-muted-foreground">{r.c}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeading eyebrow="FAQ" title="Frequently asked questions" />
        <div className="mt-10"><FaqAccordion items={faqs} /></div>
      </Section>

      {/* Contact */}
      <Section tone="muted" id="contact">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Get a Free Quote</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Tell us about your property.</h2>
            <p className="mt-4 text-muted-foreground">
              Share a few details about the service you need and we'll send a custom proposal — typically within one business day. Prefer to talk? Call (941) 500-2320.
            </p>
          </div>
          <QuoteForm sourcePage="/" />
        </div>
      </Section>

      <CtaBand title="Your Sarasota property, fully cared for." sub="Home Watch, cleanouts, hauling and concierge — one call handles it." />
    </>
  );
}
